/**
 * Tricorder house-standard FHIR-style Reference (frontend mirror of
 * tricorder-go/reference). See .claude/skills/tricorder-references.
 *
 * A resource pointer (location, role, org, …) that may be internal (one we
 * hold), external (another org's), or legacy free text. Resolution is ALWAYS
 * optional: `localId` returns null — never throws — for anything that is not an
 * internal relative reference, and `resolveReference` does a `.find()` over the
 * already-fetched pool that may be undefined. The UI always renders at least the
 * display label and never crashes on a dangling pointer.
 */

export interface ReferenceIdentifier {
  system: string;
  value: string;
}

export interface Reference {
  /** "Location/123" (ours, relative) or "https://other.ch/fhir/Location/456" (external, absolute). */
  reference?: string;
  /** Business key for a known external resource with no resolvable URL yet. */
  identifier?: ReferenceIdentifier;
  /** Free-text human label; always safe to render. A Reference with only this is valid (legacy). */
  display?: string;
}

export type ReferenceKind = 'internal' | 'external' | 'display_only' | 'empty';

/** Internal reference to a resource we hold, with a cached display label. */
export function internalRef(resourceType: string, id: string, display?: string): Reference {
  const ref: Reference = { reference: `${resourceType}/${id}` };
  if (display) ref.display = display;
  return ref;
}

/**
 * The explicit "cleared" sentinel: an empty (zero) Reference. Sent to mean "clear
 * this pointer" — distinct from omitting the field ("leave unchanged"), which a
 * pointer/null cannot express. The backend recognises it via Reference.IsZero()
 * (tricorder-go/reference); a profile_field_set event with this value = cleared.
 */
export const CLEARED_REFERENCE: Reference = {};

/** True when nothing usable is set. */
export function isEmptyReference(ref: Reference | null | undefined): boolean {
  return !ref || (!ref.reference && !ref.identifier?.value && !ref.display);
}

/**
 * Parse the internal id out of a relative "<resourceType>/<id>" reference.
 * Returns null for an absolute URL (external), a different type, an
 * identifier-only, or a display-only reference — a valid "no internal resource"
 * state, never an error.
 */
export function localId(ref: Reference | null | undefined, resourceType: string): string | null {
  const s = ref?.reference;
  if (!s || s.includes('://')) return null;
  const prefix = `${resourceType}/`;
  if (!s.startsWith(prefix)) return null;
  const id = s.slice(prefix.length);
  if (!id || id.includes('/')) return null;
  return id;
}

/** Structural classification (independent of whether an internal ref resolves). */
export function referenceKind(ref: Reference | null | undefined): ReferenceKind {
  if (ref?.reference) return ref.reference.includes('://') ? 'external' : 'internal';
  if (ref?.identifier?.value) return 'external';
  if (ref?.display) return 'display_only';
  return 'empty';
}

/** Best human label, in resolution order: display → identifier value → reference → "". */
export function referenceLabel(ref: Reference | null | undefined): string {
  if (!ref) return '';
  return ref.display ?? ref.identifier?.value ?? ref.reference ?? '';
}

export interface ResolvedReference<T> {
  label: string;
  kind: ReferenceKind;
  /** Present only when an internal reference resolved against the pool (optional join). */
  local?: T;
}

/**
 * Resolve a reference against an already-fetched pool — an OPTIONAL join: an
 * internal id that isn't in the pool yields `local: undefined` and falls back to
 * the label. Never throws, never drops.
 */
export function resolveReference<T extends { id: string }>(
  ref: Reference | null | undefined,
  resourceType: string,
  pool: readonly T[],
): ResolvedReference<T> {
  const id = localId(ref, resourceType);
  const local = id ? pool.find((x) => x.id === id) : undefined;
  return { label: referenceLabel(ref), kind: referenceKind(ref), local };
}

/** Value-equality by exact wire shape (nil-safe). */
export function referenceEquals(a: Reference | null | undefined, b: Reference | null | undefined): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/**
 * A canonical identity key for change-detection. JSON.stringify of a typed tuple
 * (rather than a delimiter-joined string) so distinct identities can never
 * collide via a separator that appears in an id / identifier value. The leading
 * tag keeps the three identity kinds disjoint.
 */
function targetKey(r: Reference | null | undefined): string {
  if (!r || isEmptyReference(r)) return '';
  if (r.reference) return JSON.stringify(['ref', r.reference]);
  if (r.identifier && (r.identifier.system || r.identifier.value)) {
    return JSON.stringify(['id', r.identifier.system, r.identifier.value]);
  }
  if (r.display) return JSON.stringify(['disp', r.display]);
  return '';
}

/**
 * Identity equality for change-detection (mirror of Go reference.SameTarget):
 * ignores the `display` cache label when a stronger identity field exists, and
 * treats nil / a zero Reference as the same "absent" state. Use this — not
 * referenceEquals — to decide whether a pointer actually changed, so a stale
 * display (e.g. a renamed org role) doesn't trip a spurious update.
 */
export function referenceSameTarget(
  a: Reference | null | undefined,
  b: Reference | null | undefined,
): boolean {
  return targetKey(a) === targetKey(b);
}
